/**
 * HtmlToPdf — invoke contract for the `HtmlToPdf` Lambda service
 * (apps/api/src/lambda/HtmlToPdf), a dedicated HTML/URL → PDF render service
 * built on puppeteer-core + @sparticuz/chromium.
 *
 * Callers invoke the Lambda directly (no HTTP endpoint) via
 * apps/api/src/utils/htmlToPdfInvoke.ts. The Lambda never throws on business
 * failures — it always resolves to an HtmlToPdfResult so callers (flow steps,
 * agent tools) get a typed success/error shape.
 */

export const HTML_TO_PDF_FORMAT = {
  A4: 'A4',
  LETTER: 'Letter',
} as const;

export type HtmlToPdfFormat =
  (typeof HTML_TO_PDF_FORMAT)[keyof typeof HTML_TO_PDF_FORMAT];

export const HTML_TO_PDF_DISPOSITION = {
  INLINE: 'inline',
  ATTACHMENT: 'attachment',
} as const;

export type HtmlToPdfDisposition =
  (typeof HTML_TO_PDF_DISPOSITION)[keyof typeof HTML_TO_PDF_DISPOSITION];

export const HTML_TO_PDF_DEFAULTS = {
  FORMAT: HTML_TO_PDF_FORMAT.A4,
  LANDSCAPE: false,
  FILE_NAME: 'document',
  DISPOSITION: HTML_TO_PDF_DISPOSITION.ATTACHMENT,
} as const;

export const HTML_TO_PDF_LIMITS = {
  /** Hard cap on the generated PDF size — larger files aren't safe to email. */
  MAX_PDF_SIZE_BYTES: 20 * 1024 * 1024,
  /** Navigation/render timeout for both `page.goto` and `page.setContent`. */
  RENDER_TIMEOUT_MS: 20_000,
  /** Presigned GET URL expiry for the uploaded PDF. */
  PRESIGNED_URL_EXPIRY_SECONDS: 60 * 60,
} as const;

export interface HtmlToPdfRequest {
  /** Raw HTML to render. Mutually exclusive with `url`. */
  html?: string;
  /** URL to navigate to and render. Mutually exclusive with `html`. */
  url?: string;
  /** Base file name (no extension). Defaults to HTML_TO_PDF_DEFAULTS.FILE_NAME. */
  fileName?: string;
  /** Page format. Defaults to HTML_TO_PDF_DEFAULTS.FORMAT ("A4"). */
  format?: HtmlToPdfFormat;
  /** Defaults to HTML_TO_PDF_DEFAULTS.LANDSCAPE (false). */
  landscape?: boolean;
  /**
   * `Content-Disposition` for the presigned download URL. Defaults to
   * HTML_TO_PDF_DEFAULTS.DISPOSITION ("attachment") — i.e. leaving this
   * unset preserves today's browser-downloads-it behavior.
   */
  disposition?: HtmlToPdfDisposition;
}

export interface HtmlToPdfSuccessResult {
  success: true;
  s3Key: string;
  /** Time-limited GET URL — see HTML_TO_PDF_LIMITS.PRESIGNED_URL_EXPIRY_SECONDS. */
  presignedUrl: string;
  fileName: string;
  sizeBytes: number;
  /** Not populated in Phase 1 — always undefined. See LAMBDAS.md for rationale. */
  pageCount?: number;
}

export interface HtmlToPdfErrorResult {
  success: false;
  error: string;
}

export type HtmlToPdfResult = HtmlToPdfSuccessResult | HtmlToPdfErrorResult;

/**
 * Type-guard for narrowing HtmlToPdfResult. Prefer this over a plain
 * `result.success` control-flow check — this repo's tsconfig does not set
 * `strictNullChecks`, under which discriminated-union narrowing on a bare
 * property check is unreliable; an explicit type predicate narrows correctly
 * regardless.
 */
export function isHtmlToPdfSuccess(
  result: HtmlToPdfResult,
): result is HtmlToPdfSuccessResult {
  return result.success === true;
}
